import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Lock, Mail } from "lucide-react";
import { api, formatApiError, imgSrc, inr } from "@/lib/api";
import { useStore } from "@/context/StoreContext";

function loadRazorpay() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

export function openRazorpay({ order, payOrder, form, onDone, onFail }) {
  window.Razorpay({
    key: payOrder.key_id,
    amount: payOrder.amount,
    currency: payOrder.currency || "INR",
    order_id: payOrder.razorpay_order_id,
    name: "Knot & Cast",
    description: `Order ${order.order_number}`,
    prefill: { name: form.name, email: form.email, contact: form.phone },
    theme: { color: "#C86A51" },
    handler: onDone,
  }).open();
}

const EMPTY_FORM = { name: "", email: "", phone: "", address: "", city: "", pincode: "", note: "" };

export default function Checkout() {
  const { items, subtotal, shipping, clear } = useStore();
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY_FORM);
  const [method, setMethod] = useState("enquiry");
  const [payConfig, setPayConfig] = useState(null);
  const [placing, setPlacing] = useState(false);

  useEffect(() => {
    api
      .get("/payments/config")
      .then((r) => {
        setPayConfig(r.data);
        if (r.data.enabled) setMethod("razorpay");
      })
      .catch(() => setPayConfig({ enabled: false }));
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const valid =
    form.name.trim().length >= 2 &&
    /\S+@\S+\.\S+/.test(form.email) &&
    form.phone.replace(/\D/g, "").length >= 10 &&
    form.address.trim().length >= 6 &&
    form.city.trim().length >= 2 &&
    form.pincode.trim().length >= 5;

  const finish = (order) => {
    clear();
    navigate(`/order/${order.id}`);
  };

  const placeOrder = async () => {
    if (!valid || placing) return;
    setPlacing(true);
    try {
      const { data: order } = await api.post("/orders", {
        customer: {
          name: form.name.trim(),
          email: form.email.trim(),
          phone: form.phone.trim(),
          address: form.address.trim(),
          city: form.city.trim(),
          pincode: form.pincode.trim(),
        },
        items: items.map((i) => ({ product_id: i.product_id, qty: i.qty })),
        payment_method: method,
        note: form.note,
      });

      if (method !== "razorpay") {
        toast.success("Enquiry received — we will write to you shortly");
        finish(order);
        return;
      }

      try {
        const { data: payOrder } = await api.post("/payments/create-order", { amount: order.total });
        const ok = await loadRazorpay();
        if (!ok) throw new Error("Could not reach Razorpay");
        window.Razorpay({
          key: payOrder.key_id,
          amount: payOrder.amount,
          currency: payOrder.currency || "INR",
          order_id: payOrder.razorpay_order_id,
          name: "Knot & Cast",
          description: `Order ${order.order_number}`,
          prefill: { name: form.name, email: form.email, contact: form.phone },
          theme: { color: "#C86A51" },
          handler: async (res) => {
            try {
              await api.post("/payments/verify", { order_id: order.id, ...res });
              toast.success("Payment received — thank you!");
            } catch (e) {
              toast.error(formatApiError(e));
            }
            finish(order);
          },
          modal: {
            ondismiss: () => {
              toast.info("Payment window closed — your order is saved and you can retry");
              finish(order);
            },
          },
        }).open();
      } catch (payErr) {
        toast.error(formatApiError(payErr));
        finish(order);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setPlacing(false);
    }
  };

  if (items.length === 0) {
    return (
      <div className="grid min-h-[70vh] place-items-center px-6 pt-24 text-center" data-testid="checkout-empty">
        <div>
          <p className="font-display text-4xl">Your cart is empty.</p>
          <p className="mt-3 text-sm text-stone2">Add a piece or two before checking out.</p>
          <Link to="/shop" className="btn-solid mt-8" data-testid="checkout-empty-shop-btn">
            Browse the collection
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-24 md:pt-32" data-testid="checkout-page">
      <div className="mx-auto max-w-7xl px-5 pb-24 md:px-8">
        <p className="label-mono">Almost yours</p>
        <h1 className="mt-3 font-display text-5xl tracking-tight sm:text-6xl">Checkout</h1>

        <div className="mt-12 grid gap-14 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <p className="label-mono">Your details</p>
            <div className="mt-6 grid gap-5 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <label className="label-mono mb-2 block" htmlFor="co-name">Full name</label>
                <input id="co-name" className="input-base" value={form.name} onChange={set("name")} placeholder="Your name" data-testid="checkout-input-name" />
              </div>
              <div>
                <label className="label-mono mb-2 block" htmlFor="co-email">Email</label>
                <input id="co-email" type="email" className="input-base" value={form.email} onChange={set("email")} placeholder="you@example.com" data-testid="checkout-input-email" />
              </div>
              <div>
                <label className="label-mono mb-2 block" htmlFor="co-phone">Phone / WhatsApp</label>
                <input id="co-phone" className="input-base" value={form.phone} onChange={set("phone")} placeholder="98xxxxxxxx" data-testid="checkout-input-phone" />
              </div>
              <div className="sm:col-span-2">
                <label className="label-mono mb-2 block" htmlFor="co-address">Address</label>
                <input id="co-address" className="input-base" value={form.address} onChange={set("address")} placeholder="House / street / landmark" data-testid="checkout-input-address" />
              </div>
              <div>
                <label className="label-mono mb-2 block" htmlFor="co-city">City</label>
                <input id="co-city" className="input-base" value={form.city} onChange={set("city")} placeholder="City" data-testid="checkout-input-city" />
              </div>
              <div>
                <label className="label-mono mb-2 block" htmlFor="co-pincode">PIN code</label>
                <input id="co-pincode" className="input-base" value={form.pincode} onChange={set("pincode")} placeholder="6 digits" data-testid="checkout-input-pincode" />
              </div>
              <div className="sm:col-span-2">
                <label className="label-mono mb-2 block" htmlFor="co-note">Note for the studio (optional)</label>
                <textarea id="co-note" rows={3} className="input-base resize-none" value={form.note} onChange={set("note")} placeholder="Gift wrap? Custom colours? Return gifts or a bulk order? Tell us here." data-testid="checkout-input-note" />
              </div>
            </div>

            <p className="label-mono mt-12">How would you like to pay?</p>
            <div className="mt-6 space-y-3">
              {payConfig === null ? (
                <p className="label-mono">Checking payment options…</p>
              ) : (
                <>
                  <label className={`block cursor-pointer border p-5 transition-colors ${method === "razorpay" ? "border-ink bg-white" : "border-stone-300 hover:border-ink"}`} data-testid="pay-mode-razorpay">
                    <div className="flex items-start gap-4">
                      <input type="radio" name="pay-method" className="mt-1 accent-[#C86A51]" checked={method === "razorpay"} onChange={() => payConfig.enabled && setMethod("razorpay")} disabled={!payConfig.enabled} data-testid="pay-radio-razorpay" />
                      <div>
                        <p className="flex items-center gap-2 font-semibold">
                          <Lock size={14} className="text-terracotta" /> Pay online now
                        </p>
                        <p className="mt-1 text-sm text-stone2">
                          {payConfig.enabled
                            ? "UPI, cards and netbanking — secured by Razorpay"
                            : "Online payments launch soon — use an order enquiry meanwhile"}
                        </p>
                      </div>
                    </div>
                  </label>
                  <label className={`block cursor-pointer border p-5 transition-colors ${method === "enquiry" ? "border-ink bg-white" : "border-stone-300 hover:border-ink"}`} data-testid="pay-mode-enquiry">
                    <div className="flex items-start gap-4">
                      <input type="radio" name="pay-method" className="mt-1 accent-[#C86A51]" checked={method === "enquiry"} onChange={() => setMethod("enquiry")} data-testid="pay-radio-enquiry" />
                      <div>
                        <p className="flex items-center gap-2 font-semibold">
                          <Mail size={14} className="text-terracotta" /> Order enquiry — pay after confirmation
                        </p>
                        <p className="mt-1 text-sm text-stone2">
                          No payment now. The studio confirms availability and payment within a day.
                        </p>
                      </div>
                    </div>
                  </label>
                </>
              )}
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="border border-line bg-white p-8 lg:sticky lg:top-28" data-testid="checkout-summary">
              <p className="label-mono">Your order</p>
              <div className="mt-6 space-y-4" data-testid="checkout-items">
                {items.map((i) => (
                  <div key={i.product_id} className="flex items-center gap-4">
                    <img src={imgSrc(i.image)} alt={i.name} className="h-16 w-16 border border-line object-cover" />
                    <div className="flex-1">
                      <p className="font-display text-lg leading-tight">{i.name}</p>
                      <p className="font-mono text-[11px] uppercase tracking-[0.15em] text-stone2">Qty {i.qty}</p>
                    </div>
                    <p className="text-sm font-semibold">{inr(i.price * i.qty)}</p>
                  </div>
                ))}
              </div>
              <div className="mt-6 space-y-1.5 border-t border-line pt-5 text-sm">
                <div className="flex justify-between"><span className="text-stone2">Subtotal</span><span>{inr(subtotal)}</span></div>
                <div className="flex justify-between"><span className="text-stone2">Shipping</span><span>{shipping === 0 ? "Free" : inr(shipping)}</span></div>
                <div className="flex justify-between border-t border-line pt-3 font-display text-2xl"><span>Total</span><span data-testid="checkout-total">{inr(subtotal + shipping)}</span></div>
              </div>
              <button
                onClick={placeOrder}
                disabled={!valid || placing}
                className="btn-solid mt-8 w-full"
                data-testid="place-order-btn"
              >
                {placing ? "Placing order…" : method === "razorpay" ? `Pay ${inr(subtotal + shipping)} securely` : "Send order enquiry"}
              </button>
              {!valid && (
                <p className="mt-3 text-center font-mono text-[11px] uppercase tracking-[0.15em] text-stone-400">
                  Fill in your details to continue
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
